import React from 'react';
import PropTypes from 'prop-types';
import { safeRest } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
var PROGRESS_BAR_COLORS = Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});
var useStyles = createUseStyles(function (theme) {
  var radius = theme.radius,
      palette = theme.palette,
      motion = theme.motion;
  var progressBar = {
    borderRadius: radius.zero,
    height: {
      sm: 4,
      md: 8,
      lg: 16
    },
    fillColor: palette.primary.main,
    trackColor: palette.muted.main
  };
  return {
    wrenchProgressBar: function wrenchProgressBar(_ref) {
      var color = _ref.color,
          size = _ref.size;
      return {
        width: '100%',
        background: progressBar.trackColor,
        borderRadius: progressBar.borderRadius,
        $wrenchProgressBarFill: {
          background: palette[color].main,
          height: progressBar.height[size]
        }
      };
    },
    wrenchProgressBarFill: function wrenchProgressBarFill(_ref2) {
      var color = _ref2.color,
          size = _ref2.size;
      return {
        transition: "width 350ms ".concat(motion.curve.ease),
        height: progressBar.height[size],
        borderRadius: progressBar.borderRadius,
        background: palette[color].main
      };
    }
  };
});
var _excluded = ["percent", "size", "color"];
/*
 * @todo need a title / aria-label attached to progress bar
 * https://dequeuniversity.com/rules/axe/4.1/aria-progressbar-name?application=axeAPI
 */

var ProgressBar = function ProgressBar(_ref) {
  var percent = _ref.percent,
      size = _ref.size,
      color = _ref.color,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();
  var classes = useStyles({
    color: color,
    size: size,
    theme: theme
  });
  var progressWidthStyle = {
    width: "".concat(percent, "%")
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: classes.wrenchProgressBar,
    role: "progressbar",
    "aria-valuenow": percent,
    "aria-valuemin": "0",
    "aria-valuemax": "100"
  }, safeRest(rest)), /*#__PURE__*/React.createElement("div", {
    style: progressWidthStyle,
    className: classes.wrenchProgressBarFill
  }));
};

ProgressBar.displayName = "ProgressBar";
ProgressBar.propTypes = {
  /**
   * 0 to 100 percentage
   */
  percent: PropTypes.number.isRequired,

  /**
   * Size variants for progress
   */
  size: PropTypes.oneOf(SIZES),

  /**
   * Color variants
   */
  color: PropTypes.oneOf(PROGRESS_BAR_COLORS)
};
ProgressBar.defaultProps = {
  size: 'md',
  color: 'success'
};
ProgressBar.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "ProgressBar",
  "props": {
    "size": {
      "defaultValue": {
        "value": "'md'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "SIZES"
      },
      "required": false,
      "description": "Size variants for progress"
    },
    "color": {
      "defaultValue": {
        "value": "'success'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "PROGRESS_BAR_COLORS"
      },
      "required": false,
      "description": "Color variants"
    },
    "percent": {
      "type": {
        "name": "number"
      },
      "required": true,
      "description": "0 to 100 percentage"
    }
  }
};
export { ProgressBar };